import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { consolidations as api } from '../utils/api';
import { fmtBRL, fmtDate, ORDER_STATUS_PT } from '../utils/helpers';
import { fmtIn, parseBRL, parseRate } from '../utils/payments';
import { useToast, ToastContainer } from '../hooks/useToast';
import { fobStatus, FOB_META, fobShort } from '../utils/fob';
import { BatchUpdate, SharedCosts, Clearance } from '../components/ConsolidationOps';
import { RESULTS } from '../components/QualityInspections';

// ═══════════════════════════════════════════════════════════════════════════
// Consolidações — vários pedidos (fornecedores diferentes) numa PI só, via
// trading. O pagamento é feito à trading e rateado entre os pedidos: cada
// pedido recebe a sua parte como pagamento e o saldo dele baixa sozinho.
// ═══════════════════════════════════════════════════════════════════════════
const BASIS = {
  fob:     { label: 'Proporcional ao FOB', hint: 'Cada pedido recebe a mesma % do seu FOB — ideal para o sinal (ex.: 30% de tudo).' },
  balance: { label: 'Proporcional ao saldo', hint: 'Distribui pelo que falta pagar em cada pedido — ideal para o saldo final.' },
  manual:  { label: 'Manual', hint: 'Você informa quanto vai para cada pedido.' },
};
const CAT = { fornecedor: 'Fornecedor (FOB)', frete: 'Frete', impostos: 'Impostos (numerário R$)', despachante: 'Despachante (R$)',
  armazenagem: 'Armazenagem (R$)', taxas: 'Taxas / outras (R$)' };
const LOCAL = ['impostos', 'despachante', 'armazenagem', 'taxas'];
const DOC_TYPES = { pi: 'PI', invoice: 'Invoice', di: 'DI / DUIMP', nf: 'NF de refaturamento', numerario: 'Numerário', comprovante: 'Comprovante', outro: 'Outro' };
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 1000) / 10 : 0);

function StatusPill({ status }) {
  const closed = status === 'closed';
  return <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
    background: closed ? 'var(--gray-100)' : '#E0F2FE', color: closed ? 'var(--gray-600)' : '#075985' }}>{closed ? 'Fechada' : 'Aberta'}</span>;
}

export default function Consolidations() {
  const { id } = useParams();
  if (id) return <ConsolidationDetail key={id} id={id} />;
  return <ConsolidationList />;
}

// ── Lista ───────────────────────────────────────────────────────────────────
function ConsolidationList() {
  const navigate = useNavigate();
  const { toasts, toast } = useToast();
  const [list, setList] = useState(null);
  const [showNew, setShowNew] = useState(false);
  useEffect(() => { api.list().then(setList).catch(() => setList([])); }, []);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">🔗 Consolidações</div>
          <div className="page-subtitle">Pedidos de vários fornecedores numa PI só (trading) — pagamento único, rateado entre os pedidos</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>+ Nova consolidação</button>
      </div>
      <div className="card" style={{ padding: 0 }}>
        {list === null ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-400)' }}>⏳</div>
          : list.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔗</div>
              <div className="empty-title">Nenhuma consolidação</div>
              <div className="empty-sub">Crie uma para juntar pedidos feitos através de uma trading e pagar o sinal de todos de uma vez.</div>
            </div>
          ) : (
            <div className="table-wrap" style={{ border: 'none' }}>
              <table>
                <thead><tr>
                  <th>Código</th><th>Trading</th><th>PI</th><th>Pedidos</th><th style={{ textAlign: 'right' }}>FOB total</th>
                  <th style={{ textAlign: 'right' }}>Saldo</th><th>Status</th>
                </tr></thead>
                <tbody>
                  {list.map(c => (
                    <tr key={c.id} onClick={() => navigate(`/consolidations/${c.id}`)} data-testid="cons-row">
                      <td className="mono">{c.code}{c.name && <div style={{ fontFamily: 'var(--font)', fontSize: 11, color: 'var(--gray-500)' }}>{c.name}</div>}</td>
                      <td className="bold">{c.trading_name || '—'}</td>
                      <td className="mono">{c.pi_number || '—'}</td>
                      <td>{c.order_count}<div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{c.order_refs || ''}</div></td>
                      <td className="mono" style={{ textAlign: 'right' }}>{fmtIn(c.currency, c.fob)}</td>
                      <td className="mono" style={{ textAlign: 'right', fontWeight: 700, color: c.balance > 0.01 ? '#C53030' : '#276749' }}>{fmtIn(c.currency, c.balance)}</td>
                      <td><StatusPill status={c.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
      {showNew && <NewConsolidationModal onClose={() => setShowNew(false)} toast={toast}
        onCreated={c => navigate(`/consolidations/${c.id}`)} />}
      <ToastContainer toasts={toasts} />
    </div>
  );
}

// Seleção de pedidos disponíveis (mesma moeda, ainda sem consolidação)
function OrderPicker({ currency, selected, onToggle, exclude = [] }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.available(currency).then(setRows).catch(() => setRows([])); }, [currency]);
  if (rows === null) return <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>⏳ carregando pedidos…</div>;
  const list = rows.filter(r => !exclude.includes(r.id));
  if (!list.length) return <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>Nenhum pedido em {currency} disponível (todos já estão consolidados ou ainda são cotação).</div>;
  return (
    <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius)' }}>
      {list.map(o => (
        <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderBottom: '1px solid var(--gray-100)', cursor: 'pointer', fontSize: 13 }}>
          <input type="checkbox" checked={selected.includes(o.id)} onChange={() => onToggle(o.id)} aria-label={`Selecionar ${o.reference}`} />
          <span className="mono" style={{ fontWeight: 700 }}>{o.reference}</span>
          <span style={{ flex: 1 }}>{o.supplier_name}</span>
          <span className="tag">{o.collection || '—'}</span>
          <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{o.order_type === 'sample' ? '🟠 Sample' : '🔵 Bulk'}</span>
          <span className="mono" style={{ fontSize: 12 }}>{o.negotiated_price ? fmtIn(o.currency || currency, o.negotiated_price) : '—'}</span>
        </label>
      ))}
    </div>
  );
}

function NewConsolidationModal({ onClose, onCreated, toast }) {
  const [form, setForm] = useState({ name: '', trading_name: '', pi_number: '', currency: 'USD', notes: '' });
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  async function save() {
    if (!form.trading_name.trim()) return toast('Informe a trading', 'error');
    setSaving(true);
    try { onCreated(await api.create({ ...form, order_ids: selected })); }
    catch (e) { toast(e.response?.data?.error || 'Erro ao criar', 'error'); setSaving(false); }
  }
  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div className="modal-title">🔗 Nova consolidação</div>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-grid" style={{ marginBottom: 16 }}>
            <div className="form-group"><label>Trading *</label>
              <input value={form.trading_name} aria-label="Trading" placeholder="Ex.: Yiwu Trading Co." onChange={e => set('trading_name', e.target.value)} /></div>
            <div className="form-group"><label>Nº da PI</label>
              <input value={form.pi_number} aria-label="Número da PI" placeholder="Ex.: PI-2026-0099" onChange={e => set('pi_number', e.target.value)} /></div>
            <div className="form-group"><label>Nome / referência</label>
              <input value={form.name} aria-label="Nome da consolidação" placeholder="Ex.: SS27 — jeans + malha" onChange={e => set('name', e.target.value)} /></div>
            <div className="form-group"><label>Moeda</label>
              <select value={form.currency} aria-label="Moeda" onChange={e => { set('currency', e.target.value); setSelected([]); }}>
                <option value="USD">USD</option><option value="RMB">RMB</option>
              </select>
              <span className="form-hint">Só pedidos na mesma moeda entram na consolidação.</span></div>
          </div>
          <div className="section-title">PEDIDOS ({selected.length} selecionado{selected.length === 1 ? '' : 's'})</div>
          <OrderPicker currency={form.currency} selected={selected}
            onToggle={id => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]))} />
          <div className="form-group" style={{ marginTop: 14 }}><label>Observações</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Condições de pagamento da trading, prazo, contato…" /></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? '⏳' : '✅ Criar consolidação'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Detalhe ─────────────────────────────────────────────────────────────────
function ConsolidationDetail({ id }) {
  const navigate = useNavigate();
  const { toasts, toast } = useToast();
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState(null);
  const [adding, setAdding] = useState(null);     // [] de ids selecionados
  const [showPay, setShowPay] = useState(false);

  const load = useCallback(() => api.get(id).then(setC).catch(() => setC(false)), [id]);
  useEffect(() => { load(); }, [load]);
  if (c === null) return <div className="page" style={{ textAlign: 'center', color: 'var(--gray-400)' }}>⏳</div>;
  if (c === false) return <div className="page">Consolidação não encontrada. <Link to="/consolidations">Voltar</Link></div>;

  const cur = c.currency;
  const t = c.totals;
  const run = async (fn, okMsg) => {
    try { const d = await fn(); if (d && d.id) setC(d); else await load(); if (okMsg) toast(okMsg, 'success'); return true; }
    catch (e) { toast(e.response?.data?.error || 'Erro', 'error'); return false; }
  };

  const kpi = (label, value, sub, color) => (
    <div style={{ flex: '1 1 160px', padding: '12px 14px', background: 'var(--gray-50)', borderRadius: 'var(--radius)', border: '1px solid var(--gray-200)' }}>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 18, color: color || 'var(--pantone)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{sub}</div>}
    </div>
  );

  return (
    <div className="page">
      <div className="page-header">
        <div className="gap-8">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/consolidations')}>← Consolidações</button>
          <div>
            <div className="page-title" style={{ fontFamily: 'var(--font-mono)', fontSize: 18 }}>{c.code} <StatusPill status={c.status} /></div>
            <div className="page-subtitle">
              🏢 {c.trading_name || 'Trading não informada'}{c.pi_number ? ` · PI ${c.pi_number}` : ''}{c.name ? ` · ${c.name}` : ''} · {cur}
              {c.phase && <> · 🚦 <b>{ORDER_STATUS_PT[c.phase] || c.phase}</b>{Object.keys(c.phases || {}).length > 1 &&
                <span style={{ color: 'var(--gray-400)' }}> ({Object.entries(c.phases).map(([st, n]) => `${n} ${ORDER_STATUS_PT[st] || st}`).join(' · ')})</span>}</>}
            </div>
          </div>
        </div>
        <div className="action-group">
          <button className="btn btn-ghost btn-sm" onClick={() => setEdit({ name: c.name || '', trading_name: c.trading_name || '', pi_number: c.pi_number || '', notes: c.notes || '' })}>✏️ Editar</button>
          <button className="btn btn-ghost btn-sm" onClick={() => run(() => api.update(c.id, { status: c.status === 'closed' ? 'open' : 'closed' }))}>
            {c.status === 'closed' ? '↩ Reabrir' : '✔ Fechar'}
          </button>
          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }}
            onClick={async () => { if (window.confirm(`Excluir ${c.code}? Os pedidos voltam a ser avulsos.`) && await run(() => api.remove(c.id))) navigate('/consolidations'); }}>🗑️</button>
        </div>
      </div>

      {edit && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="form-grid">
            <div className="form-group"><label>Trading</label><input value={edit.trading_name} aria-label="Editar trading" onChange={e => setEdit({ ...edit, trading_name: e.target.value })} /></div>
            <div className="form-group"><label>Nº da PI</label><input value={edit.pi_number} aria-label="Editar PI" onChange={e => setEdit({ ...edit, pi_number: e.target.value })} /></div>
            <div className="form-group"><label>Nome</label><input value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} /></div>
            <div className="form-group"><label>Observações</label><input value={edit.notes} onChange={e => setEdit({ ...edit, notes: e.target.value })} /></div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setEdit(null)}>Cancelar</button>
            <button className="btn btn-primary btn-sm" onClick={async () => { if (await run(() => api.update(c.id, edit), 'Consolidação atualizada')) setEdit(null); }}>💾 Salvar</button>
          </div>
        </div>
      )}
      {c.notes && !edit && <div style={{ fontSize: 13, color: 'var(--gray-600)', background: 'var(--gray-50)', borderRadius: 'var(--radius)', padding: 10, marginBottom: 16 }}>💬 {c.notes}</div>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }} data-testid="cons-kpis">
        {kpi('FOB total', fmtIn(cur, t.fob), `${c.orders.length} pedido${c.orders.length === 1 ? '' : 's'}`)}
        {kpi('Pago à trading (FOB)', fmtIn(cur, t.paid), `${t.paid_pct}% do FOB · ${fmtBRL(t.paid_brl)} desembolsado`)}
        {kpi('Saldo fornecedor', fmtIn(cur, t.balance), t.balance > 0.01 ? 'a pagar' : 'quitado', t.balance > 0.01 ? '#C53030' : '#276749')}
        {(t.freight_due > 0 || t.freight_paid > 0) && kpi('Frete', fmtIn(cur, t.freight_paid), `de ${fmtIn(cur, t.freight_due)} previsto`)}
        {(() => { const due = LOCAL.reduce((s, k) => s + (t.local?.[k]?.due || 0), 0); const paid = LOCAL.reduce((s, k) => s + (t.local?.[k]?.paid || 0), 0);
          return due > 0 || paid > 0 ? kpi('Desembaraço (R$)', fmtBRL(paid), `pago de ${fmtBRL(due)} previsto · impostos + despesas`) : null; })()}
        {t.custo_efetivo_brl > 0 && kpi('Custo estimado', fmtBRL(t.custo_efetivo_brl), c.refaturamento ? `NF: ${fmtBRL(c.refaturamento.net_cost_brl)} líquido` : 'todos os pedidos, líquido de créditos')}
      </div>

      {/* Pedidos */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--pantone)', flex: 1 }}>📦 Pedidos da consolidação</div>
          <button className="btn btn-ghost btn-sm" onClick={() => setAdding([])}>+ Adicionar pedido</button>
        </div>
        <table className="table">
          <thead><tr>
            <th>Pedido</th><th>Fornecedor</th><th>Status</th><th style={{ textAlign: 'right' }}>FOB</th><th style={{ textAlign: 'right' }}>% da PI</th>
            <th style={{ textAlign: 'right' }}>Pago</th><th style={{ textAlign: 'right' }}>Saldo</th><th>🏭 FOB</th><th style={{ width: 40 }}></th>
          </tr></thead>
          <tbody>
            {c.orders.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 20 }}>Nenhum pedido. Use “+ Adicionar pedido”.</td></tr>}
            {c.orders.map(o => {
              const f = fobStatus({ ...o, fob_ready_date: o.fob_ready?.date, fob_ready_original: o.fob_ready?.original, fob_ready_actual: o.fob_ready?.actual });
              const m = FOB_META[f.status];
              return (
                <tr key={o.id} data-testid="cons-order-row">
                  <td><Link to={`/orders/${o.id}`} className="mono" style={{ fontWeight: 700 }}>{o.reference}</Link>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{o.order_type === 'sample' ? '🟠 Sample' : '🔵 Bulk'}{o.collection ? ` · ${o.collection}` : ''}</div></td>
                  <td>{o.supplier_name}</td>
                  <td style={{ fontSize: 12 }}>{ORDER_STATUS_PT[o.status] || o.status}
                    <div style={{ fontSize: 10, color: 'var(--gray-400)' }}>{o.exchange_rate ? `R$ ${Number(o.exchange_rate).toFixed(4)}` : ''}
                      {o.quality && <span title={`Vistoria: ${RESULTS[o.quality]?.label}`}> · {RESULTS[o.quality]?.icon}</span>}</div></td>
                  <td className="mono" style={{ textAlign: 'right' }}>{fmtIn(cur, o.fob)}</td>
                  <td className="mono" style={{ textAlign: 'right', color: 'var(--gray-500)' }}>{pct(o.fob, t.fob)}%</td>
                  <td className="mono" style={{ textAlign: 'right', color: 'var(--pantone)' }}>{fmtIn(cur, o.paid)}<div style={{ fontSize: 10, color: 'var(--gray-400)' }}>{pct(o.paid, o.fob)}%</div></td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 700, color: o.settled ? '#276749' : '#C53030' }}>{o.settled ? '✓ quitado' : fmtIn(cur, o.balance)}</td>
                  <td>{f.status === 'none' ? <span style={{ color: 'var(--gray-300)' }}>—</span>
                    : <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>{fmtDate(f.date)} · {fobShort(f)}</span>}</td>
                  <td><button className="btn btn-ghost btn-sm btn-icon" title="Tirar da consolidação"
                    onClick={() => window.confirm(`Tirar ${o.reference} da consolidação?`) && run(() => api.removeOrder(c.id, o.id), `${o.reference} retirado`)}>✕</button></td>
                </tr>
              );
            })}
            {c.orders.length > 0 && (
              <tr style={{ background: 'var(--accent-light, #FAF5F6)', fontWeight: 700 }}>
                <td colSpan={3}>TOTAL DA PI</td>
                <td className="mono" style={{ textAlign: 'right' }}>{fmtIn(cur, t.fob)}</td><td></td>
                <td className="mono" style={{ textAlign: 'right' }}>{fmtIn(cur, t.paid)}</td>
                <td className="mono" style={{ textAlign: 'right', color: t.balance > 0.01 ? '#C53030' : '#276749' }}>{fmtIn(cur, t.balance)}</td>
                <td colSpan={2}></td>
              </tr>
            )}
          </tbody>
        </table>
        {adding && (
          <div style={{ padding: 16, borderTop: '1px solid var(--gray-100)', background: 'var(--gray-50)' }}>
            <OrderPicker currency={cur} selected={adding} exclude={c.orders.map(o => o.id)}
              onToggle={oid => setAdding(s => (s.includes(oid) ? s.filter(x => x !== oid) : [...s, oid]))} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setAdding(null)}>Cancelar</button>
              <button className="btn btn-primary btn-sm" disabled={!adding.length}
                onClick={async () => { if (await run(() => api.addOrders(c.id, adding), 'Pedido(s) adicionado(s)')) setAdding(null); }}>
                Adicionar {adding.length || ''}
              </button>
            </div>
          </div>
        )}
      </div>

      {c.orders.length > 0 && <BatchUpdate key={c.orders.map(o => o.id).join()} c={c} run={run} />}
      {c.orders.length > 0 && <SharedCosts c={c} run={run} />}

      {/* Pagamentos à trading */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--pantone)', flex: 1 }}>💰 Pagamentos à trading</div>
          <button className="btn btn-primary btn-sm" onClick={() => setShowPay(true)} disabled={!c.orders.length}>+ Registrar pagamento consolidado</button>
        </div>
        {showPay && <GroupPaymentForm c={c} toast={toast} onClose={() => setShowPay(false)}
          onSaved={d => { setC(d); setShowPay(false); toast('Pagamento registrado e rateado entre os pedidos', 'success'); }} />}
        <table className="table">
          <thead><tr><th>Data</th><th>Categoria</th><th style={{ textAlign: 'right' }}>Valor</th><th>Câmbio</th><th style={{ textAlign: 'right' }}>R$</th><th>Rateio</th><th>Comprovante</th><th style={{ width: 40 }}></th></tr></thead>
          <tbody>
            {c.payments.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 20 }}>Nenhum pagamento consolidado.</td></tr>}
            {c.payments.map(p => (
              <tr key={p.id} data-testid="cons-payment-row">
                <td>{fmtDate(p.payment_date)}{p.notes && <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{p.notes}</div>}</td>
                <td style={{ fontSize: 12 }}>{CAT[p.category] || p.category}{p.is_final ? ' ✓ final' : ''}</td>
                <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>{fmtIn(p.currency, p.amount_foreign)}</td>
                <td className="mono">{p.currency === 'BRL' ? <span style={{ color: 'var(--gray-300)' }}>—</span> : `R$ ${Number(p.exchange_rate).toFixed(4)}`}</td>
                <td className="mono" style={{ textAlign: 'right', color: 'var(--pantone)' }}>{fmtBRL(p.amount_brl)}</td>
                <td style={{ fontSize: 11 }}>
                  <div style={{ color: 'var(--gray-500)', marginBottom: 2 }}>{BASIS[p.basis]?.label}</div>
                  {p.splits.map(s => <div key={s.id}><Link to={`/orders/${s.quotation_id}`} className="mono">{s.reference}</Link> {fmtIn(p.currency, s.amount_foreign)}</div>)}
                </td>
                <td>{p.receipt_filename ? <a href={`/uploads/${p.receipt_filename}`} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>📎 {p.receipt_original_name || 'comprovante'}</a> : <span style={{ color: 'var(--gray-300)' }}>—</span>}</td>
                <td><button className="btn btn-ghost btn-sm btn-icon" title="Excluir pagamento consolidado" style={{ color: 'var(--error)' }}
                  onClick={() => window.confirm(`Excluir o pagamento de ${fmtIn(p.currency, p.amount_foreign)}? A parte de cada pedido também é excluída.`) && run(() => api.deletePayment(p.id), 'Pagamento excluído')}>🗑️</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Clearance c={c} run={run} />
      <ConsolidationDocs c={c} run={run} toast={toast} />
      <ToastContainer toasts={toasts} />
    </div>
  );
}

// ── Pagamento consolidado com prévia do rateio ──────────────────────────────
export function GroupPaymentForm({ c, onClose, onSaved, toast }) {
  const [form, setForm] = useState({ payment_date: new Date().toISOString().slice(0, 10), category: 'fornecedor', amount: '', pct: '',
    rate: '', brl: '', basis: 'fob', notes: '', is_final: false });
  const [rateFrom, setRateFrom] = useState('rate');      // último campo editado: câmbio ou R$
  const [manual, setManual] = useState({});              // quotation_id -> valor (texto)
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const amount = parseBRL(form.amount);
  const local = LOCAL.includes(form.category);          // numerário em reais: sem câmbio
  const cur = local ? 'BRL' : c.currency;
  const loc = c.totals.local?.[form.category] || {};
  const base = local ? (loc.due || 0) : form.category === 'frete' ? c.totals.freight_due : c.totals.fob;
  const balance = local ? (loc.balance || 0) : form.category === 'frete' ? c.totals.freight_balance : c.totals.balance;
  const rate = local ? 1 : rateFrom === 'brl' ? (amount > 0 ? parseBRL(form.brl) / amount : 0) : parseRate(form.rate);
  const orderBal = o => (local ? (o.local?.[form.category]?.balance || 0) : form.category === 'frete' ? o.freight_balance : o.balance);
  const manualSplits = c.orders.map(o => ({ quotation_id: o.id, amount: parseBRL(manual[o.id]) }));
  const manualSum = Math.round(manualSplits.reduce((s, x) => s + x.amount, 0) * 100) / 100;

  // Prévia do rateio pelo servidor (mesma conta que vai ser gravada)
  useEffect(() => {
    if (!(amount > 0)) { setPreview(null); setErr(''); return; }
    if (form.basis === 'manual') { setPreview(null); setErr(''); return; }
    const h = setTimeout(() => {
      api.previewPayment(c.id, { amount_foreign: amount, basis: form.basis, category: form.category })
        .then(p => { setPreview(p); setErr(''); })
        .catch(e => { setPreview(null); setErr(e.response?.data?.error || 'Erro no rateio'); });
    }, 250);
    return () => clearTimeout(h);
  }, [amount, form.basis, form.category, c.id]);

  function applyPct(v) {
    set('pct', v);
    const p = parseBRL(v);
    if (p > 0 && base > 0) set('amount', (Math.round(base * p) / 100).toFixed(2).replace('.', ','));
  }

  async function save() {
    if (!(amount > 0)) return toast('Informe o valor pago', 'error');
    if (!(rate > 0)) return toast('Informe o câmbio ou o valor pago em R$', 'error');
    if (form.basis === 'manual' && Math.abs(manualSum - amount) > 0.01) return toast('A soma do rateio manual precisa fechar com o valor pago', 'error');
    setSaving(true);
    try {
      const d = await api.addPayment(c.id, {
        amount_foreign: amount, exchange_rate: rate, entry_mode: rateFrom, payment_date: form.payment_date,
        category: form.category, basis: form.basis, notes: form.notes, is_final: form.is_final ? '1' : '0',
        ...(form.basis === 'manual' ? { splits: manualSplits } : {}),
      }, receipt);
      onSaved(d);
    } catch (e) { toast(e.response?.data?.error || 'Erro ao registrar', 'error'); setSaving(false); }
  }

  const lbl = { fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 3 };
  const rows = form.basis === 'manual'
    ? c.orders.map(o => ({ quotation_id: o.id, reference: o.reference, supplier_name: o.supplier_name, fob: o.fob,
        balance_before: orderBal(o) }))
    : (preview?.splits || []);

  return (
    <div style={{ padding: 16, background: 'var(--accent-light, #FAF5F6)', borderBottom: '1px solid var(--gray-100)' }} data-testid="group-payment-form">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <div><label style={lbl}>Data</label><input type="date" value={form.payment_date} aria-label="Data do pagamento" onChange={e => set('payment_date', e.target.value)} /></div>
        <div><label style={lbl}>Categoria</label>
          <select value={form.category} aria-label="Categoria" onChange={e => set('category', e.target.value)}>
            {Object.entries(CAT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div><label style={lbl}>% do {local ? 'previsto' : form.category === 'frete' ? 'frete' : 'FOB'} (atalho)</label>
          <input value={form.pct} aria-label="Percentual do FOB" placeholder="Ex.: 30" onChange={e => applyPct(e.target.value)} />
          <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 2 }}>de {fmtIn(cur, base)} · saldo {fmtIn(cur, balance)}</div></div>
        <div><label style={lbl}>Valor pago ({cur}) *</label>
          <input value={form.amount} aria-label="Valor pago" placeholder="0,00" onChange={e => { set('amount', e.target.value); set('pct', ''); }} /></div>
        {!local && <>
        <div><label style={lbl}>Câmbio (R$)</label>
          <input value={rateFrom === 'rate' ? form.rate : (rate > 0 ? rate.toFixed(4) : '')} aria-label="Câmbio" placeholder="5,4321"
            onChange={e => { setRateFrom('rate'); set('rate', e.target.value); }} /></div>
        <div><label style={lbl}>ou valor debitado em R$</label>
          <input value={rateFrom === 'brl' ? form.brl : (rate > 0 && amount > 0 ? (rate * amount).toFixed(2).replace('.', ',') : '')} aria-label="Valor em reais" placeholder="0,00"
            onChange={e => { setRateFrom('brl'); set('brl', e.target.value); }} /></div>
        </>}
      </div>

      <div style={{ marginTop: 12 }}>
        <label style={lbl}>Rateio entre os pedidos</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.entries(BASIS).map(([k, b]) => (
            <button key={k} type="button" onClick={() => set('basis', k)} title={b.hint}
              className={`btn btn-sm ${form.basis === k ? 'btn-primary' : 'btn-ghost'}`}>{b.label}</button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>{BASIS[form.basis].hint}</div>
      </div>

      {rows.length > 0 && (
        <table className="table" style={{ marginTop: 10, background: 'white' }} data-testid="split-preview">
          <thead><tr><th>Pedido</th><th>Fornecedor</th><th style={{ textAlign: 'right' }}>FOB</th><th style={{ textAlign: 'right' }}>Saldo atual</th>
            <th style={{ textAlign: 'right' }}>Vai receber</th><th style={{ textAlign: 'right' }}>Saldo depois</th></tr></thead>
          <tbody>
            {rows.map(r => {
              const amt = form.basis === 'manual' ? parseBRL(manual[r.quotation_id]) : r.amount;
              const after = Math.round((r.balance_before - amt) * 100) / 100;
              return (
                <tr key={r.quotation_id}>
                  <td className="mono">{r.reference}</td><td>{r.supplier_name}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{fmtIn(c.currency, r.fob)}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{fmtIn(cur, r.balance_before)}</td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 700, color: 'var(--pantone)' }}>
                    {form.basis === 'manual'
                      ? <input value={manual[r.quotation_id] || ''} aria-label={`Valor para ${r.reference}`} placeholder="0,00" style={{ width: 110, textAlign: 'right' }}
                          onChange={e => setManual(m => ({ ...m, [r.quotation_id]: e.target.value }))} />
                      : fmtIn(cur, amt)}
                  </td>
                  <td className="mono" style={{ textAlign: 'right', color: after < -0.01 ? '#C53030' : 'var(--gray-600)' }}>
                    {fmtIn(cur, after)}{after < -0.01 && <div style={{ fontSize: 10 }}>paga além do saldo</div>}
                  </td>
                </tr>
              );
            })}
            {form.basis === 'manual' && (
              <tr style={{ fontWeight: 700 }}><td colSpan={4}>Soma do rateio</td>
                <td className="mono" style={{ textAlign: 'right', color: Math.abs(manualSum - amount) > 0.01 ? '#C53030' : '#276749' }}>{fmtIn(cur, manualSum)}</td>
                <td style={{ fontSize: 11 }}>{Math.abs(manualSum - amount) > 0.01 ? `falta ${fmtIn(cur, amount - manualSum)}` : '✓ fecha'}</td></tr>
            )}
          </tbody>
        </table>
      )}
      {err && <div style={{ marginTop: 8, fontSize: 12, color: '#C53030' }}>⚠ {err}</div>}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
        <input value={form.notes} aria-label="Observação do pagamento" placeholder="Observação (ex.: sinal 30%, swift nº…)" onChange={e => set('notes', e.target.value)} style={{ flex: '1 1 240px' }} />
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>📎 {receipt ? receipt.name : 'Comprovante'}</button>
        <input ref={fileRef} type="file" style={{ display: 'none' }} data-testid="group-receipt" onChange={e => setReceipt(e.target.files?.[0] || null)} />
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }} title="Marca a categoria como quitada em todos os pedidos (realizado prevalece sobre o previsto)">
          <input type="checkbox" checked={form.is_final} onChange={e => set('is_final', e.target.checked)} /> Pagamento final
        </label>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{!local && rate > 0 && amount > 0 ? `= ${fmtBRL(rate * amount)}` : ''}</span>
        <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>Cancelar</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? '⏳' : '💾 Registrar e ratear'}</button>
      </div>
    </div>
  );
}

// ── Documentos da consolidação (PI, invoices, comprovantes) ─────────────────
function ConsolidationDocs({ c, run, toast }) {
  const [type, setType] = useState('pi');
  const [num, setNum] = useState('');
  const ref = useRef();
  async function upload(files) {
    if (!files.length) return;
    await run(() => api.addDocuments(c.id, files, { doc_type: type, doc_number: num }), 'Documento anexado');
    setNum('');
  }
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--pantone)', flex: 1 }}>🧾 PI e documentos da trading</div>
        <select value={type} onChange={e => setType(e.target.value)} aria-label="Tipo do documento" style={{ width: 130 }}>
          {Object.entries(DOC_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input value={num} onChange={e => setNum(e.target.value)} placeholder="Nº (opcional)" aria-label="Número do documento" style={{ width: 150 }} />
        <button className="btn btn-ghost btn-sm" onClick={() => ref.current?.click()}>📎 Anexar</button>
        <input ref={ref} type="file" multiple style={{ display: 'none' }} data-testid="cons-doc-input"
          onChange={e => { upload(Array.from(e.target.files || [])); e.target.value = ''; }} />
      </div>
      <table className="table">
        <tbody>
          {c.documents.length === 0 && <tr><td style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 16, fontSize: 13 }}>Anexe a PI consolidada — ela aparece também nos downloads de cada pedido.</td></tr>}
          {c.documents.map(d => (
            <tr key={d.id}>
              <td style={{ width: 110 }}><span className="tag">{DOC_TYPES[d.doc_type] || d.doc_type}</span></td>
              <td className="mono">{d.doc_number || '—'}</td>
              <td><a href={`/uploads/${d.filename}`} target="_blank" rel="noreferrer">📎 {d.original_name}</a></td>
              <td style={{ fontSize: 12, color: 'var(--gray-400)' }}>{fmtDate(d.created_at)}</td>
              <td style={{ width: 40 }}><button className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--error)' }} title="Excluir documento"
                onClick={() => window.confirm(`Excluir ${d.original_name}?`) && run(() => api.deleteDocument(d.id))}>🗑️</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
